import {
  ComponentType,
  isValidElement,
  ReactElement,
  ReactNode,
  useCallback,
  useMemo,
  useState,
  forwardRef,
} from "react";
import {
  autoUpdate,
  flip,
  offset,
  shift,
  size as floatingSize,
  hide,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  Placement,
  FloatingPortal,
  FloatingFocusManager,
} from "@floating-ui/react";
import { Icon, type IconName } from "../Icon";
import { Input } from "../Input";
import cx from "classnames";
import "./Dropdown.scss";
import { Flex } from "../Flex";

/**
 * Represents an item in a dropdown menu. `label` can be either a string or a ReactNode
 * for custom rendering. `value` is the unique identifier or value of the item.
 */
type DropdownItem = {
  label: string | ReactNode;
  value: string;
  behavior?: "KEEP_OPEN" | "CLOSE";
  disabled?: boolean;
  href?: string;
  keywords?: string;
};

/**
 * A list of dropdown items.
 */
type DropdownItems = DropdownItem[];

/**
 * A dropdown selection can be one of two types:
 *
 *   - A simple value of type `string`.
 *   - A custom value of type `Record<string, string>`
 *
 * This enum is used to discriminate between the two kinds of values.
 */
enum DropdownSelectionType {
  SimpleValue = "simpleValue",
  FormValue = "formValue",
}

/**
 * Represents the value of a dropdown selection. This can be either a simple
 * string value or a custom form value.
 *
 * `type` contains the type of the selection (simple value or form value).
 * `value` contains the actual value of the selection. For simple values, it's a
 * `string`. For form values, it's a `Record<string, string>`.
 */
type DropdownSelectionValue =
  | { type: DropdownSelectionType.SimpleValue; value: string }
  | {
      type: DropdownSelectionType.FormValue;
      value: Record<string, string | number>;
    };

/**
 * The parameters passed to the optional `renderCustomForm` function. `onSubmit`
 * is a regular React form submit handler. `onDismiss` is used to dismiss the
 * custom form and return the dropdown to its normal state. `closeDropdown`
 * closes the dropdown popup entirely.
 */
type RenderCustomFormProps = {
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  onDismiss: () => void;
  closeDropdown: () => void;
};

/**
 * A string to represent a custom form value. It doesn't mean anything, I just
 * picked a string that's likely to be unique.
 *
 * If the user selects a custom form value that's not part of the `options`
 * list, pass this as the `selected` to indicate a custom selection.
 */
const DropdownFormValue = "@@PingDesign@@Dropdown@@FormValue@@" as const;

/**
 * See `DropdownFormValue`.
 */
type DropdownFormValueType = typeof DropdownFormValue;

/**
 * Dropdown orientation options
 */
type DropdownOrientation = "down" | "up" | "left";

type DropdownProps = {
  options: DropdownItems;
  selected?: string | null | DropdownFormValueType;
  placeholder?: string;
  onChange(value: DropdownSelectionValue): void;
  renderCustomForm?({
    onSubmit,
    onDismiss,
  }: RenderCustomFormProps): React.ReactNode;
  label?: string;
  iconName?: IconName | ReactElement | ComponentType<any>;
  iconProps?: Record<string, any>;
  disabled?: boolean;
  error?: boolean;
  className?: string;
  size?: "md" | "lg" | "icon";
  variant?: "default" | "translucent";
  orientation?: DropdownOrientation;
  offsetX?: number;
  offsetY?: number;
  customTrigger?: ReactNode;
  required?: boolean;
  openChanged?: (open: boolean) => void;
  usePortal?: boolean;
  dropdownWidth?: React.CSSProperties["width"];
  menuClasses?: string;
  triggerClasses?: string;
  triggerLabelClasses?: string;
  menuItemClasses?: string;
  customFormOpen?: boolean;
  onCustomFormOpenChange?: (open: boolean) => void;
  closeOnScroll?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  defaultOpen?: boolean;
  dropdownMaxHeight?: React.CSSProperties["maxHeight"];
};

const optionMatchesQuery = (option: DropdownItem, query: string) => {
  const haystack = [
    typeof option.label === "string" ? option.label : "",
    option.value,
    option.keywords ?? "",
  ]
    .join(" ")
    .toLowerCase();
  return haystack.includes(query);
};

/**
 * A dropdown component that allows users to select from a list of options or
 * optionally display a custom form.
 */
const Dropdown = forwardRef<HTMLDivElement, DropdownProps>(
  (
    {
      options,
      selected,
      onChange,
      placeholder,
      renderCustomForm,
      label,
      iconName,
      iconProps = {},
      disabled = false,
      error = false,
      className,
      size = "md",
      variant = "default",
      orientation = "down",
      offsetX = 0,
      offsetY = 4,
      customTrigger,
      openChanged,
      required = false,
      usePortal = true,
      dropdownWidth,
      menuClasses,
      triggerClasses,
      triggerLabelClasses,
      menuItemClasses,
      customFormOpen,
      onCustomFormOpenChange,
      closeOnScroll = true,
      searchable = false,
      searchPlaceholder = "Filter",
      defaultOpen = false,
      dropdownMaxHeight = "300px",
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const [searchQuery, setSearchQuery] = useState("");
    const visibleOptions = useMemo(() => {
      const query = searchQuery.trim().toLowerCase();
      if (!searchable || !query) return options;
      return options.filter((option) => optionMatchesQuery(option, query));
    }, [options, searchable, searchQuery]);
    const isCustomFormControlled = customFormOpen !== undefined;
    const [internalCustomFormOpen, setInternalCustomFormOpen] = useState(false);
    const isDisplayingCustomForm = isCustomFormControlled
      ? !!customFormOpen
      : internalCustomFormOpen;
    const setIsDisplayingCustomForm = useCallback(
      (next: boolean) => {
        if (isCustomFormControlled) {
          onCustomFormOpenChange?.(next);
        } else {
          setInternalCustomFormOpen(next);
        }
      },
      [isCustomFormControlled, onCustomFormOpenChange],
    );
    const [focusedIndex, setFocusedIndex] = useState<number>(-1);
    const placement: Placement =
      orientation === "down"
        ? "bottom-start"
        : orientation === "left"
          ? "left-start"
          : "top-start";

    const onOpenChange = useCallback(
      (open: boolean) => {
        if (disabled) return;
        if (openChanged) openChanged(open);
        setIsOpen(open);
        // If the user has selected a custom form value, we need to force the
        // display of the custom form every time the menu is opened. If a custom
        // value is not selected, then we need to make sure the user is returned
        // to the main menu.
        if (open && selected === DropdownFormValue) {
          setIsDisplayingCustomForm(true);
        } else if (open && selected !== DropdownFormValue) {
          setIsDisplayingCustomForm(false);
        }
        // Reset focused index when closing
        if (!open) {
          setFocusedIndex(-1);
          setSearchQuery("");
          if (isCustomFormControlled) {
            onCustomFormOpenChange?.(false);
          }
        }
      },
      [
        selected,
        disabled,
        openChanged,
        isCustomFormControlled,
        onCustomFormOpenChange,
        setIsDisplayingCustomForm,
      ],
    );

    const { context, refs, floatingStyles } = useFloating({
      open: isOpen,
      onOpenChange,
      placement: placement,
      middleware: [
        offset({
          mainAxis: offsetY,
          crossAxis: offsetX,
        }),
        flip(),
        shift(),
        floatingSize({
          apply({ rects, elements }) {
            Object.assign(elements.floating.style, {
              width: dropdownWidth
                ? dropdownWidth
                : `${rects.reference.width}px`,
              maxHeight: dropdownMaxHeight,
              minHeight: searchable ? dropdownMaxHeight : "",
            });
          },
        }),
        hide(),
      ],
      whileElementsMounted: autoUpdate,
    });

    const click = useClick(context);
    const dismiss = useDismiss(context, {
      ancestorScroll: closeOnScroll,
      outsidePress: true,
      escapeKey: true,
    });
    const { getReferenceProps, getFloatingProps } = useInteractions([
      click,
      dismiss,
    ]);

    const getSelectedOptionLabel = useCallback(() => {
      if (selected === DropdownFormValue) {
        return "Custom";
      }
      if (selected) {
        const selectedOption = options.find(
          (option) => option.value === selected,
        );
        if (selectedOption) {
          return selectedOption.label;
        }
      }
      return placeholder || "No selection";
    }, [options, selected, placeholder]);

    const selectedOptionLabel = getSelectedOptionLabel();

    const onClickMenuItem = useCallback(
      (item: DropdownItem) => {
        if (disabled || item.disabled) return;
        onChange({
          type: DropdownSelectionType.SimpleValue,
          value: item.value,
        });
        if (!item?.behavior || item?.behavior === "CLOSE") {
          setIsOpen(false);
        }
      },
      [onChange, disabled],
    );

    const onCloseCustomForm = useCallback(() => {
      setIsDisplayingCustomForm(false);
    }, []);

    const onSubmitCustomForm = useCallback(
      (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (disabled) return;
        const formData = new FormData(e.currentTarget);
        const formDataAsObject: Record<string, string> = {};
        formData.forEach((value, key) => {
          formDataAsObject[key] = value.toString();
        });
        onChange({
          type: DropdownSelectionType.FormValue,
          value: formDataAsObject,
        });
      },
      [onChange, disabled],
    );

    const handleKeyboardNavigation = useCallback(
      (e: React.KeyboardEvent) => {
        if (!isOpen || visibleOptions.length === 0 || isDisplayingCustomForm)
          return;

        switch (e.key) {
          case "ArrowDown":
            e.preventDefault();
            setFocusedIndex((prev) => {
              const nextIndex = prev + 1;
              return nextIndex >= visibleOptions.length ? 0 : nextIndex;
            });
            break;
          case "ArrowUp":
            e.preventDefault();
            setFocusedIndex((prev) => {
              const nextIndex = prev - 1;
              return nextIndex < 0 ? visibleOptions.length - 1 : nextIndex;
            });
            break;
          case "Enter":
            e.preventDefault();
            if (focusedIndex >= 0 && focusedIndex < visibleOptions.length) {
              onClickMenuItem(visibleOptions[focusedIndex]);
            }
            break;
          case "Escape":
            e.preventDefault();
            setIsOpen(false);
            break;
        }
      },
      [
        isOpen,
        visibleOptions,
        focusedIndex,
        onClickMenuItem,
        isDisplayingCustomForm,
      ],
    );

    const renderIcon = () => {
      if (!iconName) return null;
      if (typeof iconName === "string") {
        return <Icon iconName={iconName as IconName} {...iconProps} />;
      }
      if (isValidElement(iconName)) {
        return iconName;
      }
      const IconComponent = iconName as ComponentType<any>;
      return <IconComponent {...iconProps} />;
    };

    const dropdownClasses = cx(
      "Dropdown",
      {
        "Dropdown--Disabled": disabled,
        "Dropdown--Error": error,
        "Dropdown--Large": size === "lg",
        "Dropdown--Icon": size === "icon",
        "Dropdown--Up": orientation === "up",
        "Dropdown--Left": orientation === "left",
        "Dropdown--Translucent": variant === "translucent",
      },
      className,
    );

    const dropdownTriggerClasses = cx(
      "Dropdown__Trigger",
      {
        "Dropdown__Trigger--IsOpen": isOpen,
        "Dropdown__Trigger--Error": error,
        "Dropdown__Trigger--Disabled": disabled,
        "Dropdown__Trigger--IconFilled":
          size === "icon" && selected !== placeholder,
      },
      triggerClasses,
    );

    const dropdownTriggerLabelClasses = cx(
      "Dropdown__Trigger__Label",
      {
        "Dropdown__Trigger__Label--Filled":
          selected !== placeholder &&
          selected !== null &&
          selected !== undefined &&
          selected !== "",
      },
      triggerLabelClasses,
    );

    const dropdownMenuClasses = cx(
      "Dropdown__Menu",
      {
        "Dropdown__Menu--Translucent": variant === "translucent",
      },
      menuClasses,
    );

    const getDropdownMenuItemClasses = (item: DropdownItem) =>
      cx(
        "Dropdown__Menu__Item",
        {
          "Dropdown__Menu__Item--Disabled": item.disabled,
        },
        menuItemClasses,
      );

    const renderLabelContent = (option: DropdownItem) => {
      return option.label;
    };

    const renderDropdownMenu = () => (
      <div
        className={dropdownMenuClasses}
        ref={refs.setFloating}
        style={floatingStyles}
        {...getFloatingProps()}
        onKeyDown={handleKeyboardNavigation}
      >
        {!isDisplayingCustomForm && (
          <>
            {searchable && (
              <div className="Dropdown__Menu__Search">
                <Input
                  name="dropdown-search"
                  placeholder={searchPlaceholder}
                  value={searchQuery}
                  autoFocus
                  useDebounce={false}
                  onChange={(_name, value) => {
                    setSearchQuery(String(value ?? ""));
                    setFocusedIndex(-1);
                  }}
                  suffix={<Icon iconName="Search" size={14} />}
                />
              </div>
            )}
            {searchable && visibleOptions.length === 0 && (
              <div className="Dropdown__Menu__Empty">No matches</div>
            )}
            {visibleOptions.map((option, index) => {
              const itemClasses = cx(getDropdownMenuItemClasses(option), {
                "Dropdown__Menu__Item--Focused": index === focusedIndex,
              });
              const handleClick = (e: React.MouseEvent) => {
                e.preventDefault();
                onClickMenuItem(option);
              };
              const refCallback = (el: HTMLElement | null) => {
                if (index === focusedIndex && el) {
                  el.scrollIntoView({ block: "nearest" });
                }
              };

              return option.href ? (
                <a
                  className={itemClasses}
                  key={option.value}
                  href={option.href}
                  onClick={handleClick}
                  ref={refCallback}
                >
                  {renderLabelContent(option)}
                </a>
              ) : (
                <div
                  className={itemClasses}
                  key={option.value}
                  onClick={handleClick}
                  ref={refCallback}
                >
                  {renderLabelContent(option)}
                </div>
              );
            })}
            {renderCustomForm && !isCustomFormControlled && (
              <div
                className={getDropdownMenuItemClasses({
                  value: DropdownFormValue,
                  label: "Custom",
                })}
                key={DropdownFormValue}
                onClick={() => !disabled && setIsDisplayingCustomForm(true)}
              >
                Custom
              </div>
            )}
          </>
        )}
        {isDisplayingCustomForm && (
          <>
            {renderCustomForm?.({
              onSubmit: onSubmitCustomForm,
              onDismiss: onCloseCustomForm,
              closeDropdown: () => setIsOpen(false),
            })}
          </>
        )}
      </div>
    );

    return (
      <div className={dropdownClasses} ref={ref}>
        {label && size !== "icon" && (
          <div
            className={cx("Dropdown__Label", {
              "Dropdown__Label--Error": error,
            })}
          >
            {label}
            {required && <span className="Dropdown__Required">*</span>}
          </div>
        )}
        {customTrigger ? (
          <div ref={refs.setReference} {...getReferenceProps()} style={
            {
              display: "inline-flex",
              width: "auto",
              height: "auto",
            }
          }>
            {customTrigger}
          </div>
        ) : (
          <div
            className={dropdownTriggerClasses}
            ref={refs.setReference}
            {...getReferenceProps()}
            tabIndex={disabled ? -1 : 0}
            onKeyDown={(e) => {
              if (disabled) return;
              if (!isOpen && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                setIsOpen(true);
              } else if (isOpen) {
                handleKeyboardNavigation(e);
              }
            }}
          >
            <Flex direction="row" align="center" gap="1">
              {iconName && (
                <div className="Dropdown__Trigger__Prefix">{renderIcon()}</div>
              )}
              {size !== "icon" && (
                <div className={dropdownTriggerLabelClasses}>
                  {typeof selectedOptionLabel === "string"
                    ? selectedOptionLabel
                    : selectedOptionLabel}
                </div>
              )}
            </Flex>
            {isOpen ? (
              <div className="Dropdown__Trigger__Suffix">
                <Icon
                  iconName="ChevronUp"
                  className="Dropdown__Trigger__Icon"
                />
              </div>
            ) : (
              <div className="Dropdown__Trigger__Suffix">
                <Icon
                  iconName="ChevronDown"
                  className="Dropdown__Trigger__Icon"
                />
              </div>
            )}
          </div>
        )}
        {isOpen &&
          !disabled &&
          (usePortal ? (
            <FloatingPortal>
              <FloatingFocusManager
                context={context}
                modal={false}
                initialFocus={-1}
              >
                {renderDropdownMenu()}
              </FloatingFocusManager>
            </FloatingPortal>
          ) : (
            renderDropdownMenu()
          ))}
      </div>
    );
  },
);

export {
  Dropdown,
  DropdownSelectionType,
  type DropdownSelectionValue,
  DropdownFormValue,
  type DropdownItem,
  type DropdownFormValueType,
};
